/**
 * @file 登录页（账号密码 → 短信二次验证）
 * 三端共用一个实现，差异只有端名与说明文案。所有请求都经 auth store → api-client。
 *
 * 版式：左栏表单、右栏一叠词卡。右栏不是插画——词卡就是这个产品的最小工作单位，
 * 拿它当版式元素比放抽象图形更诚实，也避免把登录页做成营销落地页。
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useAuthState } from '../auth/context';
import { useToast } from './Toast';
import { describeError, type ErrorText } from './errors';
import { useCountdown } from './useCountdown';
import logoUrl from '../../brand/logo.svg';

/** 登录页属性。 */
export interface LoginPageProps {
  /** 端名，如「教师上课端」。 */
  endName: string;
  /** 卡片副标题。 */
  subtitle?: string;
  /** 账号输入框提示，便于联调。 */
  accountHint?: string;
}

/**
 * 右栏示例词表：五个词覆盖「名词—动词—复习—遗忘」四种课堂动作。
 * 只作版式示例，不来自任何学员数据，因此标「示例」。
 */
const WORD_LIST: { word: string; pos: string; ipa: string; def: string }[] = [
  { word: 'whale', pos: 'n.', ipa: '/weɪl/', def: '鲸' },
  { word: 'memorize', pos: 'v.', ipa: '/ˈmeməraɪz/', def: '记住，背下来' },
  { word: 'vocabulary', pos: 'n.', ipa: '/vəˈkæbjələri/', def: '词汇量' },
  { word: 'review', pos: 'v.', ipa: '/rɪˈvjuː/', def: '复习，回顾' },
  { word: 'forget', pos: 'v.', ipa: '/fəˈɡet/', def: '忘记' },
];

/**
 * @what 第二步的提示语：只有服务端确认发出后才说「已发送」
 * @param smsSendState 短信发送结果
 * @param maskedPhone 脱敏手机号
 * @return string 提示语
 */
function challengeHint(smsSendState: 'idle' | 'sent' | 'failed', maskedPhone: string | null | undefined): string {
  const phone = maskedPhone ?? '绑定手机号';
  if (smsSendState === 'sent') return `验证码已发到 ${phone}，5 分钟内有效。没收到就点下面重新发送。`;
  if (smsSendState === 'failed') return `${phone} 刚发过验证码，这次没发出去。等倒计时结束再重新发送。`;
  return `正在向 ${phone} 发送验证码…`;
}

/**
 * @what 登录页：第一步账号密码、第二步短信验证码
 * @param props.endName 端名
 * @param props.subtitle 副标题
 * @param props.accountHint 账号提示
 * @return JSX
 */
export function LoginPage({ endName, subtitle, accountHint }: LoginPageProps) {
  const auth = useAuth();
  const state = useAuthState();
  const toast = useToast();
  const navigate = useNavigate();

  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorText | null>(null);

  const cooldown = useCountdown(state.smsCooldownUntil);
  const isChallenge = state.phase === 'challenging';

  useEffect(() => {
    if (state.phase === 'authenticated') navigate('/', { replace: true });
  }, [state.phase, navigate]);

  /**
   * @what 提交账号密码
   * @param event 表单事件
   * @return Promise<void> 无返回值
   */
  const handleLogin = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.login(account.trim(), password);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  /**
   * @what 提交短信验证码
   * @param event 表单事件
   * @return Promise<void> 无返回值
   */
  const handleVerify = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await auth.verify(code.trim());
      toast.success('登录成功');
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  /**
   * @what 重发验证码；429 时按 AUTH_SMS_SEND_TOO_FREQUENT 提示并继续倒计时
   * @return Promise<void> 无返回值
   */
  const handleResend = async (): Promise<void> => {
    setError(null);
    try {
      await auth.resendSms();
      toast.success('验证码已重新发送');
    } catch (cause) {
      setError(describeError(cause));
    }
  };

  // 联调验证码来自后端（sms.provider=mock 才返回），生产恒为空
  const devCodeVisible = Boolean(state.devCode);

  return (
    <div className="lx-auth">
      <section className="lx-auth__main">
        <div className="lx-auth__inner">
          <header className="lx-auth__brand">
            <img src={logoUrl} alt="词鲸英语" />
            <div>
              <div className="lx-auth__name">词鲸英语</div>
              <div className="lx-auth__latin">CIJINGYU ENGLISH</div>
            </div>
          </header>

          {/* key 随步骤变化，让 React 重新挂载、步骤动效重放一次 */}
          <div className="lx-auth__form lx-auth__step" key={isChallenge ? 'challenge' : 'password'}>
            <p className="lx-auth__end">{endName}</p>
            <h1 className="lx-auth__title">{isChallenge ? '输入验证码' : '登录'}</h1>
            <p className="lx-auth__sub">
              {isChallenge
                ? challengeHint(state.smsSendState, state.maskedPhone)
                : subtitle ?? '用工作账号登录。教师与管理员需要再过一次短信验证。'}
            </p>

            {isChallenge ? (
              <form onSubmit={handleVerify}>
                <div className="lx-field">
                  <label className="lx-field__label" htmlFor="lx-code">
                    6 位验证码
                  </label>
                  <input
                    id="lx-code"
                    className="lx-input lx-input--code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="••••••"
                    value={code}
                    onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    autoFocus
                  />
                </div>

                {devCodeVisible ? (
                  <div className="lx-devcode">
                    联调验证码 <code>{state.devCode}</code>
                    <span>（生产环境不返回）</span>
                  </div>
                ) : null}

                {error ? (
                  <div className="lx-auth__error">
                    <strong>{error.message}</strong>
                    <span className="lx-mono">
                      {error.code ?? 'CLIENT_ERROR'}
                      {error.traceId ? ` · 追踪号 ${error.traceId}` : ''}
                    </span>
                  </div>
                ) : null}

                <button type="submit" className="lx-btn lx-btn--primary lx-btn--block" disabled={busy || code.length !== 6}>
                  {busy ? '验证中…' : '验证并进入'}
                </button>

                <div className="lx-auth__actions">
                  <button
                    type="button"
                    className="lx-btn lx-btn--text"
                    onClick={() => {
                      auth.cancelChallenge();
                      setError(null);
                      setCode('');
                    }}
                  >
                    换账号登录
                  </button>
                  <button type="button" className="lx-btn lx-btn--text" disabled={cooldown > 0} onClick={() => void handleResend()}>
                    {cooldown > 0 ? `${cooldown}s 后可重发` : '重新发送验证码'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleLogin}>
                <div className="lx-field">
                  <label className="lx-field__label" htmlFor="lx-account">
                    账号
                  </label>
                  <input
                    id="lx-account"
                    className="lx-input"
                    autoComplete="username"
                    placeholder={accountHint ?? '账号或手机号'}
                    value={account}
                    onChange={(event) => setAccount(event.target.value)}
                    autoFocus
                    required
                  />
                </div>

                <div className="lx-field">
                  <label className="lx-field__label" htmlFor="lx-password">
                    密码
                  </label>
                  <input
                    id="lx-password"
                    className="lx-input"
                    type="password"
                    autoComplete="current-password"
                    placeholder="请输入密码"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </div>

                {error ? (
                  <div className="lx-auth__error">
                    <strong>{error.message}</strong>
                    <span className="lx-mono">
                      {error.code ?? 'CLIENT_ERROR'}
                      {error.traceId ? ` · 追踪号 ${error.traceId}` : ''}
                    </span>
                  </div>
                ) : null}

                <button type="submit" className="lx-btn lx-btn--primary lx-btn--block" disabled={busy}>
                  {busy ? '登录中…' : '登录'}
                </button>
              </form>
            )}
          </div>

            <footer className="lx-auth__foot">
              忘记密码请让主管重置；连续输错 5 次账号会锁定 15 分钟。
            </footer>
        </div>
      </section>

      {/* 装饰栏：不加 aria 语义，读屏用户完全不依赖它 */}
      <aside className="lx-auth__aside" aria-hidden="true">
        <div className="lx-auth__aside-head">
          <b>示例词表</b>
          <span>课堂的最小单位</span>
        </div>
        <div className="lx-auth__aside-body">
          <div className="lx-wordlist">
            <div className="lx-wordlist__head">
              <span>单词</span>
              <span>音标</span>
              <span>释义</span>
            </div>
            {WORD_LIST.map((item) => (
              <div className="lx-wordlist__row" key={item.word}>
                <span className="lx-wordlist__word">
                  {item.word}
                  <i>{item.pos}</i>
                </span>
                <span className="lx-wordlist__ipa">{item.ipa}</span>
                <span className="lx-wordlist__def">{item.def}</span>
              </div>
            ))}
          </div>
          <p className="lx-auth__captions">
            <b>课堂以词为单位</b>
            测评、复习、报告都按词统计；教师端与学员端看到的是同一份词表。
          </p>
        </div>
      </aside>
    </div>
  );
}
